import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Switch, Route, useRouteMatch, useHistory } from 'react-router-dom';
import { push, replace } from 'redux-first-history';
import dayjs from '@api/services/tools/dayjs';
import { parse } from 'query-string';
import { isEmpty, isNil } from 'lodash-es';

import styled, { keyframes } from 'styled-components';
import { Box, Grid } from '@material-ui/core';
import UpIcon from '../Channels/SingleChannel/upIcon.svg';

import {
    Typography,
    Container,
    TabsPanel,
    ElasticTheme as Theme,
    PrimaryButton,
    TertiaryButton,
    palette,
    Header,
    ContentLoader,
    formatNumber,
} from '@dev/ee-components';
import { CompressionFormat, DeliveryPausedReason, ExportFileFormats, DeliveryLogType } from '@dev/ee-api/ee-api-models';

import ExportModal from '../../ExportModal';
import CancelInProgressModal from './CancelInProgressModal';
import { ShowDialog } from '@api/store/dialog/actions';
import {
    LoadInprogressStatsStartDelayed,
    LoadCancelInprogressStart,
    ExportLogs,
    getLogsStore,
    getLogsInprogressStatsPausedReason,
    getLogDomainsTotalCount,
    getInProgressEmailsCount,
    ClearLogList,
} from '@api/store/logs/actions';
import Overview from './Overview';
import Log from './Log';
import InProgressAlert from './InProgressAlert';
import { inProgressStatusesArray } from './config';
import { useMobile } from '@api/services/hooks/useMobile';
import { StatisticsPaths } from '@api/services/dictionaries/paths';

const TopPanels = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    margin: 32px 0 48px 0;
`;

const Panel = styled.div<{ isMobile: boolean }>`
    padding: 24px 32px;
    width: ${(props): string => (props.isMobile ? '100%' : 'auto')};
    flex: ${(props): string => (props.isMobile ? '' : '1 1 20%')};
    margin-right: ${(props): string => (props.isMobile ? '0px' : '30px')};
    margin-bottom: ${(props): string => (props.isMobile ? '32px' : '0px')};
    background-color: #fff;
    border-radius: 4px;
    border: 1px solid ${palette.gray[3]};

    &:last-of-type {
        margin-right: 0px;
        margin-bottom: 0px;
    }
`;

const waves = keyframes`
    40% {
        transform: translateY(0px);
    }

    50% {
        transform: translateY(-5px);
    }
`;

const DotsContainer = styled.div<{ visible: boolean }>`
    visibility: ${(props): string => (props.visible ? 'visible' : 'hidden')};
    display: inline-block;
    margin-left: 5px;
`;

const Dot = styled.span`
    display: inline-block;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    margin-right: 3px;
    background-color: ${palette.jacarta[5]};
    animation: ${waves} 1.2s linear infinite;

    &:nth-child(2) {
        animation-delay: 0.4s;
    }

    &:nth-child(3) {
        animation-delay: 0.8s;
    }
`;

type LoadingDotsProps = {
    isVisible: boolean;
};

const LoadingDots: FC<LoadingDotsProps> = (props: LoadingDotsProps): JSX.Element => {
    return (
        <DotsContainer visible={props.isVisible}>
            <Dot />
            <Dot />
            <Dot />
        </DotsContainer>
    );
};

type InProgressHeaderType = {
    openInProgress(): void;
};

const InProgressHeader: FC<InProgressHeaderType> = (props: InProgressHeaderType): JSX.Element => {
    const { openInProgress } = props;
    const { t } = useTranslation();
    const logs = useSelector(getLogsStore);
    const { setLoader, inprogressStatsDelayed } = logs;
    const totalCount = inprogressStatsDelayed?.Domains?.length;
    const isMobile = useMobile();
    const dispatch = useDispatch();

    const goToStatistics = (): void => {
        dispatch(push(StatisticsPaths.StatisticsOverview));
    };

    const headerProps = {
        header: {
            title: (
                <span style={{ display: 'inline-flex' }}>
                    <Typography variant={'h1'}>{t('inprogress')}</Typography>
                    <LoadingDots isVisible={setLoader} />
                </span>
            ) as any,
        },
        breadcrumbs: {
            text: t('activity'),
            onClick: goToStatistics,
        },
        button: {
            onClick: openInProgress,
            size: 'medium' as any,
            text: isMobile ? null : t('exportinprogress'),
            variant: 'text' as any,
            disabled: totalCount ? false : true,
            startIcon: (<UpIcon />) as any,
        },
    };

    return (
        <>
            <Header {...headerProps} />
        </>
    );
};

const InProggessTopPanels = (): JSX.Element => {
    const { t } = useTranslation();
    const logs = useSelector(getLogsStore);
    const { inprogressStatsDelayed } = logs;
    const isMobile = useMobile();

    const inProgressEmails = {
        all: !isNil(inprogressStatsDelayed?.Count) ? formatNumber(inprogressStatsDelayed?.Count) : <ContentLoader mode="row" count={1} />,
        waiting: !isNil(inprogressStatsDelayed?.Count) ? (
            formatNumber(inprogressStatsDelayed?.Count - inprogressStatsDelayed?.Ready)
        ) : (
            <ContentLoader mode="row" count={1} />
        ),
        ready: !isNil(inprogressStatsDelayed?.Ready) ? formatNumber(inprogressStatsDelayed?.Ready) : <ContentLoader mode="row" count={1} />,
        sentRate: !isNil(inprogressStatsDelayed) ? (
            `${inprogressStatsDelayed?.SentRate?.toFixed(1)} /s`
        ) : (
            <ContentLoader mode="row" count={1} />
        ),
    };

    return (
        <TopPanels>
            <Panel isMobile={isMobile}>
                <Typography variant={'h1'}>{inProgressEmails.all}</Typography>
                <Typography variant={'subtitle1'} display="inline">
                    <Box color={Theme.typography.h1.color} fontWeight={Theme.typography.h1.fontWeight}>
                        {t('allinprogress')}
                    </Box>
                </Typography>
            </Panel>
            <Panel isMobile={isMobile}>
                <Typography variant={'h1'}>{inProgressEmails.waiting}</Typography>
                <Typography variant={'subtitle1'} display="inline">
                    <Box color={Theme.typography.h1.color} fontWeight={Theme.typography.h1.fontWeight}>
                        {t('allwtr')}
                    </Box>
                </Typography>
            </Panel>
            <Panel isMobile={isMobile}>
                <Typography variant={'h1'}>{inProgressEmails.ready}</Typography>
                <Typography variant={'subtitle1'} display="inline">
                    <Box color={Theme.typography.h1.color} fontWeight={Theme.typography.h1.fontWeight}>
                        {t('rts')}
                    </Box>
                </Typography>
            </Panel>
            <Panel isMobile={isMobile}>
                <Typography variant={'h1'}>{inProgressEmails.sentRate}</Typography>
                <Typography variant={'subtitle1'} display="inline">
                    <Box color={Theme.typography.h1.color} fontWeight={Theme.typography.h1.fontWeight}>
                        {t('accountsendingrate')}
                    </Box>
                </Typography>
            </Panel>
        </TopPanels>
    );
};

const InProgressPausedAlert: FC = (): JSX.Element => {
    const pausedReason = useSelector(getLogsInprogressStatsPausedReason);
    const isPaused = pausedReason && pausedReason !== DeliveryPausedReason.NotPaused;

    if (!isPaused) {
        return <></>;
    }

    return <InProgressAlert pausedReason={pausedReason} />;
};

type ExportButtonType = {
    openInProgressModal(): void;
};

const ExportButton: FC<ExportButtonType> = (props: ExportButtonType): JSX.Element => {
    const { openInProgressModal } = props;
    const { t } = useTranslation();
    const totalCount = useSelector(getLogDomainsTotalCount);

    return (
        <>
            <PrimaryButton
                id="exportlinks"
                onClick={openInProgressModal}
                size="medium"
                text={t('exportinprogress')}
                variant="text"
                disabled={!totalCount}
                startIcon={<UpIcon />}
            />
        </>
    );
};

const inProgressMobilePadding = {
    left: 16,
    right: 16,
};

const InProgress: FC = (): JSX.Element => {
    const { t } = useTranslation();
    const isMobile = useMobile();
    const dispatch = useDispatch();
    const exportModalInputRef = useRef(null);
    const match = useRouteMatch();
    const history = useHistory();
    const hasAnyInProgressEmails = useSelector(getInProgressEmailsCount);
    const [exportOptions, setExportOptions] = useState({
        channelNames: [],
        fileName: null,
        compressionFormat: CompressionFormat.Zip,
        fileFormat: ExportFileFormats.Csv,
    });

    const defaultTab = match.path === StatisticsPaths.StatisticsLog ? 1 : 0;
    const [activeTab, setActiveTab] = useState(defaultTab);

    useEffect(() => {
        const query: any = parse(history?.location.search);
        let activePage = 1;

        if (!isEmpty(query) && query.page) {
            activePage = parseInt(query.page);
        }

        dispatch(LoadInprogressStatsStartDelayed(activePage));
        return () => {
            dispatch(ClearLogList());
        };
    }, []);

    const exportInProgress = (): void => {
        const fileName = exportModalInputRef.current.value || 'export';
        const fileFormat: ExportFileFormats = exportOptions.fileFormat;
        const statuses: DeliveryLogType[] = inProgressStatusesArray;
        const from = dayjs().subtract(2, 'd').utc().hour(0).minute(0).format();

        const exportParams = {
            statuses,
            fileName,
            from,
            fileFormat,
            compressionFormat: exportOptions.compressionFormat,
        };

        dispatch(ExportLogs(exportParams));
    };

    const openCancelInProgressDialog = (): void => {
        const cancelInProgress = (): void => {
            dispatch(LoadCancelInprogressStart());
        };
        dispatch(
            ShowDialog({
                title: t('cancelinprogress'),
                body: <CancelInProgressModal text={t('cancelinprogressinfo')} />,
                destructiveLabel: t('confirm'),
                cancelLabel: 'Cancel',
                onClickDestructive: cancelInProgress,
            })
        );
    };

    const changeExportCompression = (enabled: boolean): void => {
        exportOptions.compressionFormat = enabled ? CompressionFormat.Zip : CompressionFormat.None;
        setExportOptions(exportOptions);
    };

    const changeExportFileFormat = (format: ExportFileFormats): void => {
        exportOptions.fileFormat = format;
        setExportOptions(exportOptions);
    };

    const openInProgressModal = (): void => {
        dispatch(
            ShowDialog({
                title: t('exportinprogress'),
                body: (
                    <ExportModal
                        onCompressionChange={changeExportCompression}
                        onFileFormatChange={changeExportFileFormat}
                        inputRef={exportModalInputRef}
                    />
                ),
                confirmLabel: t('export'),
                cancelLabel: t('cancel'),
                onClickConfirm: exportInProgress,
            })
        );
    };

    const tabsOnClick = (ev, newValue): void => {
        const goTo = (url: string): void => {
            dispatch(replace(url));
        };
        setActiveTab(newValue);

        const url = newValue === 1 ? StatisticsPaths.StatisticsLog : StatisticsPaths.StatisticsInprogress;
        goTo(url);
    };

    return (
        <Container paddingMobile={inProgressMobilePadding}>
            <InProgressHeader openInProgress={openInProgressModal} />
            {isMobile ? (
                <Grid container style={{ display: 'flex' }} wrap="wrap">
                    <Grid container>
                        <ExportButton openInProgressModal={openInProgressModal} />
                    </Grid>
                    <Grid container style={{ marginTop: '32px' }}>
                        <TertiaryButton
                            text={t('cancelinprogress')}
                            onClick={openCancelInProgressDialog}
                            disabled={!hasAnyInProgressEmails}
                        />
                    </Grid>
                </Grid>
            ) : (
                <TertiaryButton
                    text={t('cancelinprogress')}
                    onClick={openCancelInProgressDialog}
                    style={{ marginTop: 8 }}
                    disabled={!hasAnyInProgressEmails}
                />
            )}
            <InProgressPausedAlert />
            <InProggessTopPanels />
            <TabsPanel tabs={[{ label: t('overview') }, { label: t('details') }]} onChange={tabsOnClick} value={activeTab} />
            <Switch>
                <Route path={StatisticsPaths.StatisticsInprogress} exact component={Overview} />
                <Route path={StatisticsPaths.StatisticsLog} exact component={Log} />
            </Switch>
        </Container>
    );
};

export default InProgress;
